import React, { useMemo, useState, useEffect, useContext } from "react"
import {
  RadioLabelDescriptionInput,
  RadioWithImageBg,
  LaunchModalBuilderSection,
} from "@components/configurator"
import LabeledRadioField from "./LabeledRadioField"
import ToggleWithImageField from "./ToggleWithImageField"
import ToggleField from "./ToggleField"
import TextField from "./TextField"
import LongTextField from "./LongTextField"
import SelectWithImageField from "./SelectWithImageField"

import { useAddItemToCart } from "@lib/swell/storefront-data-hooks"
import { prepareVariantsWithOptions } from "@lib/swell/storefront-data-hooks/src/utils/product"
import { getVariant } from "@lib/swell/storefront-data-hooks/src/api/operations-swell"
import { LoadingDots } from "@components/ui"
import {
  OptionInput,
  ProductOption,
  Product,
  OptionValue,
} from "@lib/swell/storefront-data-hooks/src/types"
import { sidebarContext } from "@components/common/Layout"
import { appToast } from "@components/common/Toast"
import { getPrice } from "@lib/swell/storefront-data-hooks/src/utils/product"

export interface ProductProps {
  className?: string
  children?: any
  product: Product
  renderSeo?: boolean
  description?: string
  title?: string
}

export { FORM_ERROR, FORM_SUCCESS } from "@components/common/Form"
import { Form, FormProps } from "@components/common/Form"

interface ConfiguratorProductFormProps {
  product: string | any
  swellProduct: string | any
  variant: any
  setVariant: React.Dispatch<any>
  hideAddToCart?: boolean
  currentSelections?: Selection[]
  setCurrentSelections?: (selections: Selection[]) => void
}
export interface Selection extends OptionInput {
  id: string
  name: string
  value: string
  optionId: string
  variant: boolean
  priceForDisplay: string
  isToggle: boolean
  includedInVariant: boolean
}

export var setDefaultSelections = function (options: any) {
  return options.map((option: any) => {
    const includedInVariant = option.parent_value_ids ? false : true
    if (option.values?.length === 0) {
      // short text
      return {
        id: option.id,
        name: option.name,
        value: "",
        optionId: option.id,
        variant: false,
        priceForDisplay: "Included",
        isToggle: false,
        includedInVariant,
      }
    } else if (option.values?.length === 1) {
      // toggle
      return {
        id: option.values[0].id,
        name: option.name,
        value: "",
        optionId: option.id,
        variant: true,
        priceForDisplay: option.values[0].price
          ? getPrice(option.values[0].price, "USD")
          : "Included",
        isToggle: true,
        includedInVariant,
      }
    } else {
      return {
        id: option.values[0].id,
        name: option.name,
        value: option.values[0].name,
        optionId: option.id,
        variant: true,
        priceForDisplay: option.values[0].price
          ? getPrice(option.values[0].price, "USD")
          : "Included",
        isToggle: false,
        includedInVariant,
      }
    }
  })
}

const ConfiguratorProductForm = ({
  product,
  swellProduct,
  variant,
  setVariant,
  hideAddToCart = false,
  currentSelections,
  setCurrentSelections,
}: ConfiguratorProductFormProps) => {
  const addItem = useAddItemToCart()
  const variants = useMemo(() => prepareVariantsWithOptions(product), [product])
  const { setOpenSidebar } = useContext(sidebarContext)

  const options = product?.options

  const defaultSelections: Selection[] = setDefaultSelections(options)

  const initialValues: any = {}
  options.map((option: any) => {
    if (option.values.length === 0)
      // text field
      // initialValues[option.name] = ""
      return
    else if (option.values?.length === 1) {
      // toggle
      initialValues[option.name] = false
    } else {
      initialValues[option.name] = option.values[0].name
    }
  })

  const [loading, setLoading] = useState(false)
  const [selections, setSelections] = useState(currentSelections || defaultSelections)
  const [productOptions, setProductOptions] = useState(options)

  function updateSelections(selections: Selection[]) {
    const updatedSelections = selections.map((selection) => {
      const option = productOptions.find(
        (option: ProductOption) => option.id === selection.optionId
      )
      const optionValue = option?.values.find((value: OptionValue) => value.id === selection.id)
      if (option) {
        return {
          ...selection,
          priceForDisplay: optionValue?.price ? getPrice(optionValue.price, "USD") : "Included",
          includedInVariant: showOption(option),
        }
      }
      return selection
    })
    setCurrentSelections && setCurrentSelections(updatedSelections)
    setSelections(updatedSelections)
  }

  function updateSelectionChange(name: string, optionValueId: string, value: string) {
    const selectionToUpdate = selections.find((selection) => {
      return selection.name == name
    })

    if (selectionToUpdate) {
      let swellValue
      if (typeof value === "string") {
        swellValue = value
      } else {
        swellValue = value ? name : ""
      }

      selectionToUpdate.value = swellValue
      selectionToUpdate.id = optionValueId

      updateSelections(selections)
    }
  }

  function updateTextFieldSelectionChange(name: string, optionId: string, value: string) {
    const selectionToUpdate = selections.find((selection) => {
      return selection.name == name
    })

    if (selectionToUpdate) {
      selectionToUpdate.value = value
      selectionToUpdate.id = optionId

      updateSelections(selections)
    }
  }

  function inputChangeHandler(option: ProductOption, value: string) {
    const { name, values } = option
    const id = values.find((optionValue) => optionValue.name == value)?.id ?? option.id
    const selectionToUpdate = selections.find((selection) => {
      return selection.name == name
    })

    if (selectionToUpdate) {
      selectionToUpdate.value = value
      selectionToUpdate.id = id

      updateSelections(selections)
    }
  }

  const addToCart = async () => {
    setLoading(true)

    try {
      await addItem(
        product.id,
        1,
        selections.filter((selection) => !!selection.value)
      )
      setOpenSidebar(true)
      setLoading(false)
    } catch (err) {
      setLoading(false)
      appToast({ type: "error", message: `Unable to add product to cart. Error: ${err}` })
    }
  }

  const findSelected = (optionId: string) => {
    return selections.find((selection: any) => selection.optionId === optionId)
  }

  const showOption = (option: ProductOption) => {
    if (!option.parent_id && !option.parent_value_ids) {
      return true
    }

    if (option.parent_value_ids) {
      return option.parent_value_ids.some((parentId) =>
        selections.some(
          (selection: Selection) => parentId === selection.id && selection.includedInVariant
        )
      )
    } else {
      const parentSelection = selections.find((selection) => {
        return selection.optionId == option.parent_id
      })

      if (!parentSelection) return true
      return !!parentSelection.value
    }
  }

  useEffect(() => {
    async function setSelectedVariant() {
      const selectedVariant = await getVariant(
        swellProduct,
        selections.map((selection) =>
          selection.includedInVariant
            ? selection
            : defaultSelections.find((s) => s.optionId === selection.optionId)
        )
      )

      if (selectedVariant) {
        setVariant(selectedVariant)
      }
    }

    setSelectedVariant()
  }, [selections])

  useEffect(() => {
    setProductOptions(product.options)
  }, [product])

  return (
    <Form
      initialValues={initialValues}
      onSubmit={async (values) => {
        addToCart()
      }}
      className="mt-6"
    >
      <div className="space-y-10">
        {productOptions?.length > 0 &&
          productOptions?.map((option: ProductOption) => {
            if (!showOption(option)) return <></>
            switch (option.input_component_string) {
              case "radio_with_label_and_description":
                return (
                  <div key={option.id}>
                    <RadioLabelDescriptionInput
                      option={option}
                      // eslint-disable-next-line
                      // @ts-ignore
                      selected={findSelected(option.id).value}
                      onChange={(event: any) => {
                        inputChangeHandler(option, event)
                      }}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "toggle":
                return (
                  <div key={option.id}>
                    <ToggleField option={option} updateSelectionChange={updateSelectionChange} />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "toggle_with_image":
                return (
                  <div key={option.id}>
                    <ToggleWithImageField
                      option={option}
                      currentlyEnabled={findSelected(option.id)?.value ? true : false}
                      updateSelectionChange={updateSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "radio_with_image_bg":
                return (
                  <div key={option.id}>
                    <RadioWithImageBg
                      option={option}
                      optionValues={option.values}
                      // eslint-disable-next-line
                      // @ts-ignore
                      selected={findSelected(option.id)?.value}
                      onChange={(event: any) => {
                        inputChangeHandler(option, event)
                      }}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "dropdown":
                return (
                  <div key={option.id}>
                    <LabeledRadioField
                      option={option}
                      selected={findSelected(option.id)?.value}
                      updateSelectionChange={updateSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "text_field":
                return (
                  <div key={option.id}>
                    <TextField
                      option={option}
                      selections={selections}
                      value={findSelected(option.id)?.value}
                      updateTextFieldSelectionChange={updateTextFieldSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "long_text_field":
                return (
                  <div key={option.id}>
                    <LongTextField
                      option={option}
                      selections={selections}
                      value={findSelected(option.id)?.value}
                      updateTextFieldSelectionChange={updateTextFieldSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "select_with_image":
                return (
                  <div key={option.id}>
                    <SelectWithImageField
                      option={option}
                      selected={findSelected(option.id)?.value}
                      updateSelectionChange={updateSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              default:
                return (
                  <div key={option.id}>
                    <LabeledRadioField
                      option={option}
                      selected={findSelected(option.id)?.value}
                      updateSelectionChange={updateSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
            }
          })}
      </div>
      {hideAddToCart ? (
        <></>
      ) : (
        <div className="mt-4">
          <button
            name="add-to-cart"
            disabled={loading}
            // onClick={addToCart}
            type="submit"
            className="flex max-w-xs flex-1 items-center justify-center rounded-md border border-transparent bg-primary-600 px-8 py-3 text-base font-medium text-white hover:bg-primary-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50 sm:w-full"
          >
            Add to bag {loading && <LoadingDots />}
          </button>

          {/* <button
          type="button"
          className="ml-4 py-3 px-3 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-500"
        >
          <HeartIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
          <span className="sr-only">Add to favorites</span>
        </button> */}
        </div>
      )}
    </Form>
  )
}

export default ConfiguratorProductForm
